import { Auth } from "aws-amplify";
import { RequireNewPassword as AmplifyRequireNewPassword } from "aws-amplify-react";
import { Field, Form, Formik, FormikActions, FormikProps } from "formik";
import React, { MouseEvent } from "react";
import * as Yup from "yup";
import { AuthState, IAuthData } from "../../models/auth.models";
import PasswordValidator from "password-validator";
import {
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

interface ISignInProps {
  authData: IAuthData;
  authState: AuthState;
  onAuthStateChange: (next: AuthState, data: IAuthData) => void;
  onAuthEvent: (next: AuthState, data: IAuthData) => void;
}

interface ISignInState {
  pending: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
}

export function resetFormError(
  setErrors: (errors: { [key: string]: string }) => void,
) {
  setErrors({}); // Reset errors to an empty object
}

// Create password validator schema
const passwordSchema = new PasswordValidator();
passwordSchema
  .is()
  .min(8)
  .is()
  .max(20)
  .has()
  .uppercase()
  .has()
  .lowercase()
  .has()
  .digits()
  .has()
  .symbols()
  .has()
  .not()
  .spaces();

const RequirePasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required("Please provide your password")
    .test(
      "password-validation",
      "Password does not meet requirements",
      function (value) {
        return passwordSchema.validate(value || "", { list: false }) as boolean;
      },
    ),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});
class FormikUtils {
  static getFormikFieldClasses(
    formState: FormikProps<IAuthData>, // Formik Props
    fieldName: keyof IAuthData, // Field name from IAuthData
    size?: "sm" | "lg", // Optional size class
  ): string {
    const baseClass = "form-control";
    const sizeClass = size ? `form-control-${size}` : "";
    const validationClass =
      formState.errors[fieldName] && formState.touched[fieldName]
        ? "is-invalid"
        : "";

    return `${baseClass} ${sizeClass} ${validationClass}`.trim();
  }
}
export class RequireNewPassword extends AmplifyRequireNewPassword<
  ISignInProps,
  ISignInState
> {
  static defaultProps: ISignInProps = {
    authData: {},
    authState: "signIn",
    onAuthStateChange: () => null,
    onAuthEvent: () => null,
  };

  state: ISignInState = {
    pending: false,
    showPassword: false,
    showConfirmPassword: false,
  };

  _validAuthStates = ["requireNewPassword"];

  changePassword = (
    { password }: IAuthData,
    actions: FormikActions<IAuthData>,
  ) => {
    const user = this.props.authData;

    if (!Auth || typeof Auth.completeNewPassword !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported",
      );
    }
    Auth.completeNewPassword(user, password ?? "", {})
      .then((userResponse: { challengeName?: string }) => {
        if (userResponse.challengeName === "SMS_MFA") {
          this.changeState("confirmSignIn", userResponse);
        } else if (userResponse.challengeName === "MFA_SETUP") {
          this.changeState("TOTPSetup", userResponse);
        } else {
          this.changeState("signedIn", user);
        }
      })
      .catch((err) => actions.setStatus({ error: err.message || err }));
  };

  onBackToSignIn = (e: MouseEvent) => {
    e.preventDefault();
    this.changeState("signIn", null);
  };

  togglePasswordVisibility = () => {
    this.setState({ showPassword: !this.state.showPassword });
  };

  toggleConfirmPasswordVisibility = () => {
    this.setState({ showConfirmPassword: !this.state.showConfirmPassword });
  };

  showComponent() {
    const initialFormValues: IAuthData = {
      password: "",
      confirmPassword: "",
    };

    return (
      <div className="row mt-4">
        <Formik
          initialValues={initialFormValues}
          onSubmit={this.changePassword}
          validationSchema={RequirePasswordSchema}
        >
          {(formState) => (
            <Form
              onChange={() => resetFormError(formState.setErrors)}
              className="col-sm-4 offset-sm-4 mt-4"
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              <div className="card">
                <fieldset className="card-body p-5">
                  <h1 className="mb-4">Reset your password</h1>
                  <div className="form-group">
                    <div className="input-group">
                      <Field
                        type={this.state.showPassword ? "text" : "password"}
                        name="password"
                        className={FormikUtils.getFormikFieldClasses(
                          formState,
                          "password",
                          "lg",
                        )}
                        placeholder="New Password"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={this.togglePasswordVisibility}
                        >
                          {this.state.showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="invalid-feedback">
                      {formState.errors.password}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="input-group">
                      <Field
                        type={
                          this.state.showConfirmPassword ? "text" : "password"
                        }
                        name="confirmPassword"
                        className={FormikUtils.getFormikFieldClasses(
                          formState,
                          "confirmPassword",
                          "lg",
                        )}
                        placeholder="Confirm New Password"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={this.toggleConfirmPasswordVisibility}
                        >
                          {this.state.showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="invalid-feedback">
                      {formState.errors.confirmPassword}
                    </div>
                  </div>

                  {/* Password Requirements Checklist */}
                  <div
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "12px",
                      marginBottom: "16px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      Password Requirements:
                    </div>
                    <div style={{ fontSize: "13px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {formState.values.password &&
                        formState.values.password.length >= 8 &&
                        formState.values.password.length <= 20 ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        8-20 characters long
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {formState.values.password &&
                        /[a-z]/.test(formState.values.password) ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        At least one lowercase letter
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {formState.values.password &&
                        /[A-Z]/.test(formState.values.password) ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        At least one uppercase letter
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {formState.values.password &&
                        /\d/.test(formState.values.password) ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        At least one number
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {formState.values.password &&
                        /[^\w\s]/.test(formState.values.password) ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        At least one symbol
                      </div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {formState.values.password &&
                        !/\s/.test(formState.values.password) ? (
                          <CheckCircle
                            style={{
                              color: "#4caf50",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        ) : (
                          <Cancel
                            style={{
                              color: "#f44336",
                              fontSize: "16px",
                              marginRight: "6px",
                            }}
                          />
                        )}
                        No spaces allowed
                      </div>
                    </div>
                  </div>

                  {formState.status && formState.status.error ? (
                    <p className="text-danger">{formState.status.error}</p>
                  ) : (
                    ""
                  )}

                  <button
                    type="submit"
                    className="btn btn-lg btn-primary mt-4"
                    disabled={
                      !passwordSchema.validate(
                        formState.values.password || "",
                        { list: false },
                      ) ||
                      !formState.values.confirmPassword ||
                      formState.values.password !==
                        formState.values.confirmPassword
                    }
                  >
                    Change password
                  </button>
                  <p className="text-left mt-2">
                    <a href="#" onClick={this.onBackToSignIn}>
                      Back to Sign In
                    </a>
                  </p>
                </fieldset>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}
